import React, { createContext, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { ApolloClient, InMemoryCache, ApolloProvider, ApolloLink, HttpLink } from '@apollo/client'
import { connect } from 'react-redux'
import { withMsal as withAuth0 } from '../components/MsalAuth/withMsal'

import { logoutThunk } from '../redux/thunks/auth'
import { createAuthLink, createAuthErrorLink } from './authLinks'
import { defaultOptions } from './clientOptions'

import config from '../config'

export const ClientContext = createContext()

const ApolloClientProvider = props => {
  const auth0Ref = useRef(props.auth0)
  auth0Ref.current = props.auth0

  const logoutRef = useRef(props.logout)
  logoutRef.current = props.logout

  const clientRef = useRef(null)
  if (!clientRef.current) {
    const getAuth0 = () => auth0Ref.current

    /**
     * The error link goes FIRST: its `forward(operation)` call has to pass
     * back through the auth link so the retry goes out with the refreshed token.
     */
    const link = ApolloLink.from([
      createAuthErrorLink({ getAuth0, onLogout: () => logoutRef.current() }),
      createAuthLink({ getAuth0 }),
      new HttpLink({ uri: `${config.API_URL}` }),
    ])

    clientRef.current = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions,
    })
  }

  return (
    <ApolloProvider client={clientRef.current}>
      <ClientContext.Provider value={clientRef.current}>{props.children}</ClientContext.Provider>
    </ApolloProvider>
  )
}

const mapDispatchToProps = {
  logout: logoutThunk,
}

export const useClient = () => {
  const context = useContext(ClientContext)
  if (context === undefined) {
    throw new Error('must be within ContextProvider')
  }
  return context
}

ApolloClientProvider.propTypes = {
  logout: PropTypes.func.isRequired,
  auth0: PropTypes.object.isRequired,
}

export const Provider = connect(null, mapDispatchToProps)(withAuth0(ApolloClientProvider))
